import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  type User,
  type UserCredential,
} from "firebase/auth";
import { initializeApp } from "firebase/app";
import { markIntentionalLogout } from "@labeling-tools/auth";
import { client } from "@augmodo/lebron-browser-client";
import { client as atlasClient } from "@labeling-tools/api/atlas/client.gen";
import { client as embeddingtonClient } from "@labeling-tools/api/embeddington/client.gen";
import { client as lebronClient } from "@labeling-tools/api/lebron/generated/client.gen";
import { client as userInsightsClient } from "@labeling-tools/api/user-insights/generated/client.gen";

const firebaseConfig = JSON.parse(import.meta.env.VITE_FIREBASE_CONFIG);
initializeApp(firebaseConfig);
const auth = getAuth();
const provider = new GoogleAuthProvider();

function getCurrentUser(): Promise<User | null> {
  return new Promise((resolve) => {
    const remove = auth.onAuthStateChanged((user) => {
      remove();
      resolve(user);
    });
  });
}

// Initialize clients with base URLs
client.setConfig({
  baseUrl:
    import.meta.env.VITE_LEBRON_BASE_URL || "https://mvp-api.augmoto.com",
});

atlasClient.setConfig({
  baseUrl:
    import.meta.env.VITE_ATLAS_BASE_URL || "https://atlas-api.augmoto.com",
});

lebronClient.setConfig({
  baseUrl:
    import.meta.env.VITE_LEBRON_BASE_URL || "https://mvp-api.augmoto.com",
});

embeddingtonClient.setConfig({
  baseUrl:
    import.meta.env.VITE_EMBEDDINGTON_BASE_URL ||
    "https://embeddington.augmoto.com",
});

userInsightsClient.setConfig({
  baseUrl:
    import.meta.env.VITE_USER_INSIGHTS_BASE_URL ||
    "https://consulate-prod.augmoto.com",
});

// When running the training build, tag job-listing requests so lebron can
// scope them to training data instead of live production jobs.
function withTrainingQueryParam(request: Request): Request {
  if (import.meta.env.VITE_LABELING_ENVIRONMENT !== "TRAINING") return request;
  if (request.method !== "GET") return request;
  const url = new URL(request.url);
  if (url.pathname !== "/v1/jobs" && url.pathname !== "/v1/jobs-in-flight") {
    return request;
  }
  url.searchParams.set("testing", "true");
  return new Request(url.toString(), request);
}

// Function to setup auth headers for all clients
function setupAuthHeaders() {
  client.interceptors.request.use(async (config) => {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return withTrainingQueryParam(config);
  });

  atlasClient.interceptors.request.use(async (config) => {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return config;
  });

  lebronClient.interceptors.request.use(async (config) => {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return withTrainingQueryParam(config);
  });

  userInsightsClient.interceptors.request.use(async (config) => {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return config;
  });
}

// Setup auth headers immediately if user is already authenticated
if (auth.currentUser) {
  setupAuthHeaders();
}

// Setup auth headers when user signs in
auth.onAuthStateChanged((user) => {
  if (user) {
    setupAuthHeaders();
  }
});

async function signIn(): Promise<UserCredential> {
  return signInWithPopup(auth, provider);
}

async function signOut(): Promise<void> {
  markIntentionalLogout();
  return auth.signOut();
}

function ensureAuthHeaders(): void {
  if (auth.currentUser) {
    setupAuthHeaders();
  }
}

function waitForAuth(): Promise<void> {
  return new Promise((resolve) => {
    if (auth.currentUser) {
      resolve();
    } else {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        if (user) {
          unsubscribe();
          resolve();
        }
      });
    }
  });
}

export {
  signIn,
  signOut,
  getCurrentUser,
  auth,
  ensureAuthHeaders,
  waitForAuth,
};
